import React from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import { StickyContainer, Sticky } from 'react-sticky';

import DisplayLocation from '../../../../../../components/common/DisplayLocation';
import DisplayDate from '../../../../../../components/common/DisplayDate';
import ButtonQuantities from './ButtonQuantities';
import ButtonComponent from '../../../../../../components/common/forms/Button/ButtonComponent';
import useReserver from './useReserver';
import { ProductX } from '~/src/types/Misc';
import { Colors } from '~/src/scss/common/style';

interface Props {
  item: ProductX;
  id: string;
}

export default function ReserveProductBlock({ item, id }: Props): JSX.Element {
  const reserver = useReserver(item, id);
  const handleColor = (): string => {
    if (reserver.status === 'unsaved') {
      return Colors.lightYellow;
    } else if (reserver.status === 'error') {
      return Colors.redOrange;
    } else if (reserver.status === 'success') {
      return Colors.softGreen;
    } else {
      return 'none';
    }
  };

  const renderNotes = (notes) => {
    if (notes != null) {
      let string = '';
      const objectFormNotes = JSON.parse(notes);
      objectFormNotes.forEach((element) => {
        string += `${element.Date}: ${element.Quantities}\n`;
      });
      return <div>{string}</div>;
    }
    return '';
  };

  return (
    <Container statusColor={handleColor()}>
      <StickyContainer style={{ display: 'flex' }}>
        <Sticky>
          {({ style }): JSX.Element => (
            <div style={{ position: 'absolute', ...style, width: 300, padding: 10 }}>
              <ProductContent>
                <b>{item.productName}</b>
                {renderNotes(item.orderProduct.quantitiesNote)}
              </ProductContent>
              <QuantityWrapper>
                <Quantities>
                  <table>
                    <thead>
                      <tr>
                        <th></th>
                        <th>Du</th>
                        <th>Dp</th>
                        <th>Cu</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <CellSmall>Order</CellSmall>
                        <CellSmall>{reserver?.itemData?.orderProduct?.quantityDu}</CellSmall>
                        <CellSmall>x</CellSmall>
                        <CellSmall>x</CellSmall>
                        {/* <CellSmall>{reserver?.itemData?.orderProduct?.quantityCu}</CellSmall> */}
                      </tr>
                      <tr>
                        <CellSmall>Reserved</CellSmall>
                        <CellSmall>{reserver.duReserved}</CellSmall>
                        <CellSmall>{reserver.dpReserved}</CellSmall>
                        <CellSmall>{reserver.cuReserved}</CellSmall>
                      </tr>
                      <tr>
                        <CellSmall>Reserve Preview</CellSmall>
                        <CellSmall>{reserver.duReservePreview}</CellSmall>
                        <CellSmall>{reserver.dpReservePreview}</CellSmall>
                        <CellSmall>{reserver.cuReservePreview}</CellSmall>
                      </tr>
                      <tr>
                        <CellSmall>To Reserve</CellSmall>
                        <CellSmall>
                          {(reserver?.itemData?.orderProduct?.quantityDu ?? 0) - reserver.duReservePreview}
                        </CellSmall>
                        <CellSmall>x</CellSmall>
                        <CellSmall>x</CellSmall>
                      </tr>
                    </tbody>
                  </table>
                </Quantities>
                <MainControls>
                  {reserver.bufferTransactions.length > 0 ? (
                    <>
                      <ButtonComponent title="Save" onClick={reserver.prepareDtoSendAndUpdateScreen} />
                      <ButtonComponent
                        title="Reset"
                        variant="link"
                        onClick={reserver.clearAllBufferTransactions}
                      />
                    </>
                  ) : (
                    <span>No changes</span>
                  )}
                </MainControls>
              </QuantityWrapper>
            </div>
          )}
        </Sticky>
        <Table>
          <Head>
            <tr>
              <StyledTh>Info</StyledTh>
              <StyledTh></StyledTh>
              <StyledTh>Avaliable</StyledTh>
              <StyledTh>
                Already
                <br />
                Reserved
              </StyledTh>
              <StyledTh>
                Reserve
                <br />
                Preview
              </StyledTh>
              <StyledTh>Actions</StyledTh>
            </tr>
          </Head>
          <Body>
            {reserver.itemData.distincts.map((data) => {
              const buffer = reserver.bufferTransactions.find(
                (bufferTransaction) => bufferTransaction.tempId === data.availableQuantities.tempId,
              );
              const maxDu = Math.min(
                (data?.availableQuantities?.fullDuUnits ?? 0) + (data?.reservedQuantities?.fullDuUnits ?? 0),
                (reserver?.itemData?.orderProduct?.quantityDu ?? 0) - reserver.duReservePreview ?? 0,
              );
              const maxDp =
                (data?.availableQuantities?.singleDpUnits ?? 0) + (data?.reservedQuantities?.singleDpUnits ?? 0);
              const maxCu =
                (data?.availableQuantities?.singleCuUnits ?? 0) + (data?.reservedQuantities?.singleCuUnits ?? 0);
              return (
                <TRow key={uuid()}>
                  <StyledTd>
                    <Span>
                      <DisplayDate
                        date={data?.availableQuantities.expirationDate ?? data?.reservedQuantities.expirationDate}
                      />
                    </Span>
                    <Span>
                      <DisplayLocation
                        chamber={
                          data?.availableQuantities?.warehouseLocation?.warehouseChamber?.number ??
                          data?.reservedQuantities?.warehouseLocation?.warehouseChamber?.number
                        }
                        column={
                          data?.availableQuantities?.warehouseLocation?.column ??
                          data?.reservedQuantities?.warehouseLocation?.column
                        }
                        letter={
                          data?.availableQuantities?.warehouseLocation?.letter ??
                          data?.reservedQuantities?.warehouseLocation?.letter
                        }
                        level={
                          data?.availableQuantities?.warehouseLocation?.level ??
                          data?.reservedQuantities?.warehouseLocation?.level
                        }
                      />
                    </Span>
                    <Span>
                      {data.availableQuantities.lot ?? data.reservedQuantities.lot
                        ? data.availableQuantities.lot ?? data.reservedQuantities.lot
                        : '---'}
                    </Span>
                  </StyledTd>

                  <StyledTd>
                    <Span>DU</Span>
                    <Span>DP</Span>
                    <Span>CU</Span>
                  </StyledTd>

                  <StyledTd>
                    <Span>{data.availableQuantities.fullDuUnits}</Span>
                    <Span>{data.availableQuantities.singleDpUnits}</Span>
                    <Span>{data.availableQuantities.singleCuUnits}</Span>
                  </StyledTd>

                  <StyledTd>
                    <Span>{data.reservedQuantities.fullDuUnits}</Span>
                    <Span>{data.reservedQuantities.singleDpUnits}</Span>
                    <Span>{data.reservedQuantities.singleCuUnits}</Span>
                  </StyledTd>

                  <StyledTd>
                    <Span>{buffer?.fullDuUnits}</Span>
                    <Span>{buffer?.singleDpUnits}</Span>
                    <Span>{buffer?.singleCuUnits}</Span>
                  </StyledTd>

                  <StyledTd>
                    <ActionWrapper>
                      <span>
                        <Span>
                          <ButtonQuantities
                            tempId={data.availableQuantities.tempId}
                            maxNumber={maxDu}
                            unitType="du"
                            reserve={reserver.setQuantity}
                            clear={reserver.clearUnitFromBufferTransaction}
                          />
                        </Span>
                        <Span>
                          <ButtonQuantities
                            tempId={data.availableQuantities.tempId}
                            maxNumber={maxDp}
                            unitType="dp"
                            reserve={reserver.setQuantity}
                            clear={reserver.clearUnitFromBufferTransaction}
                          />
                        </Span>
                        <Span>
                          <ButtonQuantities
                            tempId={data.availableQuantities.tempId}
                            maxNumber={maxCu}
                            unitType="cu"
                            reserve={reserver.setQuantity}
                            clear={reserver.clearUnitFromBufferTransaction}
                          />
                        </Span>
                      </span>

                      <span>
                        <CellButton>
                          <ButtonComponent title="Reset" variant="link" onClick={reserver.clearAllUnits} />
                        </CellButton>
                      </span>
                    </ActionWrapper>
                  </StyledTd>
                </TRow>
              );
            })}
          </Body>
        </Table>
      </StickyContainer>
    </Container>
  );
}

interface ContainerProps {
  statusColor: string;
}
const Container = styled.div<ContainerProps>`
  background-color: ${(props) => props.statusColor};
  min-height: 300px;
  margin-bottom: 50px;
`;

const MainControls = styled.div``;

const Table = styled.table`
  width: 100%;
  margin-left: 300px;
  div:first-child {
  }
`;

const StyledTh = styled.th`
  display: flex
  flex-direction: column;
  border: 1px solid #333;
`;

const StyledTd = styled.td`
  border: 1px solid #333;
`;

const QuantityWrapper = styled.div``;

const ActionWrapper = styled.span`
  display: flex;
  justify-content: space-around;
  align-items: center;
`;

const Span = styled.span`
  font-size: 16px;
  height: 60px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;

  button {
    margin-left: 5px;
  }
  span {
    font-size: 18px;
  }
`;

const CellButton = styled.span`
  height: 60px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;

  button {
    width: 100px;
  }

  .btn-link {
    color: red;
  }
`;

const ProductContent = styled.div``;

const Head = styled.thead`
  th {
    text-align: center;
    width: 110px;
  }
  th:first-child {
    width: 200px !important;
    span {
      height: 20px;
    }
  }
  th:nth-child(6) {
    width: 200px !important;
  }
  th:last-child {
    width: 400px !important;
  }
`;

const Body = styled.tbody`
  td {
    text-align: center;
    width: 110px;
  }
`;

const TRow = styled.tr`
  td:first-child {
    width: 200px !important;
    span {
      height: 20px;
    }
  }
  td:last-child {
    width: 200px !important;
    opacity: 0;
  }
  &:hover td:last-child {
    opacity: 1;
  }
`;

const Quantities = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;

  p {
    padding: 5px 0;
    margin: 0;
  }
`;

const CellSmall = styled.td`
  padding: 0 10px;
`;
